import Graph from "node-dijkstra"
import { Id, point } from "@turf/helpers"
import { forEach, get, last, set } from "lodash"

import { Opening, OpeningData } from "providers/venue/types"
import {
  GeolocationFeature,
  IStep,
  IStepElement,
  NavigatableFeature,
} from "interfaces"

import { BASE_POI_DISTANCE, VERTICAL_TRAVERSAL_CATEGORY } from "../../constants"

import { getCenterFromGeometry } from "utils/geometry"
import {
  calculateDisplacement,
  findLocatedFeature,
  getOrdinalFromStep,
} from "../shared"
import { DESTINATION_MARKER_ID, ORIGIN_MARKER_ID } from "components/IndoorMap"
import { Position } from "geojson"

export const featureIsVerticalTraversalUnit = (feature) => {
  if (!feature || !("category" in feature.properties)) return false
  return VERTICAL_TRAVERSAL_CATEGORY.indexOf(feature.properties.category) > -1
}

export const featureIsOccupantWithUnit = (feature: NavigatableFeature) => {
  const { feature_type, properties } = feature
  return (
    feature_type === "occupant" && "unit_id" in properties && properties.unit_id
  )
}

export const parseGeolocationId = (id: string) => id.replaceAll(".", "|")

export const addGeolocationPOINode = (
  feature: GeolocationFeature,
  openings: Opening[],
  relationshipGraph: Graph
) => {
  const acc = {}
  const geolocationId = parseGeolocationId(String(feature.id))
  for (let i = 0; i < openings.length; i++) {
    const opening = openings[i]
    try {
      const { geometry: featureCenterGeometry } = point(
        getCenterFromGeometry(feature.geometry)
      )
      const { geometry: openingCenterGeometry } = point(
        getCenterFromGeometry(opening.geometry)
      )
      const distance =
        calculateDisplacement(featureCenterGeometry, openingCenterGeometry, {
          units: "meters",
        }) + BASE_POI_DISTANCE
      set(acc, `${opening.id}.${geolocationId}`, distance)
      set(acc, `${geolocationId}.${opening.id}`, distance)
    } catch (error) {
      console.log("error calculating distance for poi id: " + feature.id)
      continue
    }
  }

  const graph = relationshipGraph.graph
  forEach(acc, (value, key) => {
    const previousValue = graph.get(key) || []
    const newMapValue = Object.entries(value)
    // Merged previous nodes with newly added nodes connections
    relationshipGraph.addNode(key, new Map([...previousValue, ...newMapValue]))
  })
}

export const getGeolocationShortestPath = (
  feature: GeolocationFeature,
  connectedOpenings: OpeningData[],
  relationshipGraph: Graph,
  compareOpeningId: Id,
  isOrigin: boolean
): string[] | null => {
  const geolocationId = parseGeolocationId(feature.id as string)
  const graph = relationshipGraph.graph

  if (!graph.has(geolocationId))
    addGeolocationPOINode(feature, connectedOpenings, relationshipGraph)

  const shortestPath = isOrigin
    ? relationshipGraph.path(geolocationId, compareOpeningId)
    : relationshipGraph.path(compareOpeningId, geolocationId)
  return shortestPath
}

export const findShortestPath = (
  routeOrigin: NavigatableFeature,
  routeDestination: NavigatableFeature,
  relationshipGraph: Graph,
  unitConnections
): string[] | null => {
  try {
    const originUnitOpenings = findLocatedFeature(routeOrigin)
    const destinationUnitOpenings = findLocatedFeature(routeDestination)
    if (routeOrigin.feature_type === "geolocation") {
      const originUnit = findLocatedFeature(routeOrigin)
      const originUnitId = originUnit.id
      const connectedOpenings = get(unitConnections, originUnitId, [])
      return getGeolocationShortestPath(
        routeOrigin,
        connectedOpenings,
        relationshipGraph,
        destinationUnitOpenings.id,
        true
      )
    }
    if (routeDestination.feature_type === "geolocation") {
      const destinationUnit = findLocatedFeature(routeDestination)
      const destinationUnitId = destinationUnit.id
      const connectedOpenings = get(unitConnections, destinationUnitId, [])
      return getGeolocationShortestPath(
        routeDestination,
        connectedOpenings,
        relationshipGraph,
        originUnitOpenings.id,
        false
      )
    }
    return relationshipGraph.path(
      originUnitOpenings.id,
      destinationUnitOpenings.id
    )
  } catch (error) {
    console.log({ error })
  }
}

export const transformStepsToGeometriesLegacy = (steps = []) => {
  const firstStep = steps[0]
  const lastStep = steps[steps.length - 1]
  const routeOrigin: IStepElement = {
    id: ORIGIN_MARKER_ID,
    feature_type: "origin-marker",
    geometry: point(getCenterFromGeometry(firstStep.origin.geometry))?.geometry,
    properties: {
      ordinal: getOrdinalFromStep(firstStep.origin),
    },
  }
  const routeDestination: IStepElement = {
    id: DESTINATION_MARKER_ID,
    feature_type: "destination-marker",
    geometry: point(getCenterFromGeometry(lastStep.origin.geometry))?.geometry,
    properties: {
      ordinal: getOrdinalFromStep(lastStep.destination),
    },
  }

  const stepGeometries: IStepElement[] = steps.map((step, index) => {
    const { origin, intermediary, destination } = step

    // If geolocation is available at the origin in this step, use the default bearing to set the map view for the route.
    const hasGeolocationOrigin = origin?.feature_type === "geolocation"

    // check the step is the vertical step or not
    const isVerticalStep =
      origin.properties.ordinal !== destination.properties.ordinal &&
      intermediary.length > 0 &&
      intermediary.every((feature) =>
        VERTICAL_TRAVERSAL_CATEGORY.includes(feature.properties.category)
      )
    // get unit ids which are intermediaries
    const intermediaryIds = intermediary.map((unit) => unit.id)

    let stepPath = step.path
    // transform Line String apth to point if the step is the vertical step
    if (isVerticalStep) stepPath = point(step.path.geometry.coordinates[0])

    return {
      ...stepPath,
      isVerticalStep: isVerticalStep,
      isLastStep: steps.length - 1 === index,
      hasGeolocationOrigin,
      properties: {
        ordinal: getOrdinalFromStep(origin),
        intermediaryUnits: intermediaryIds,
      },
      id: `step-${index + 1}`,
    }
  })
  stepGeometries.unshift(routeOrigin)
  stepGeometries.push(routeDestination)
  return stepGeometries
}

export const createArrivalStep = (steps: IStep[]): IStep => {
  const lastStep = last(steps)
  const path = point(
    last<Position>(lastStep.path.geometry.coordinates as Position[])
  )

  return {
    goingTo: lastStep.destination,
    comingFrom: lastStep.destination,
    destination: lastStep.destination,
    origin: lastStep.destination,
    intermediary: [],
    path,
    description: { text: "Arrived at destination" },
  }
}
